import { useMemo, useCallback, useEffect } from "react";
import { Select } from "antd";
import { IconArrowDown, IconDirection, IconTime } from "@icons/index";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { useTranslateDuration } from "@hooks/useTranslateDuration";
import { TestId } from "@shared/testIds";
import {
  setDescParams,
  selectVideoCreatingParams,
  selectCreateOptions,
  DescParamsData,
  filterResolutionOptionsByRules,
} from "../videoCreatingSlice";
import ModelSelect from "./ModelSelect";

interface VideoParamsSelectProps {
  /** 是否禁用 duration 选择 */
  disabledDuration?: boolean;
  /** 是否显示 resolution 选择 */
  showResolution?: boolean;
  /** 是否显示 quantity 选择 */
  showQuantity?: boolean;
  /** 是否显示 model 选择（实际可见性还取决于当前 duration 是否有可用模型） */
  showModel?: boolean;
  /** 是否禁用所有选项 */
  disabled?: boolean;
}

export const VideoParamsSelect = ({
  disabledDuration = false,
  showResolution = false,
  showModel = true,
  disabled = false,
}: VideoParamsSelectProps) => {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectVideoCreatingParams);
  const { aspectRatio, duration, resolution, internalModelId } = params.descParams;
  const createOptions = useAppSelector(selectCreateOptions);
  const { aspectRatioOptions, durationOptions, resolutionOptions, rules } = createOptions;
  const translatedDurationOptions = useTranslateDuration(durationOptions);
  // 根据当前 duration 和 model，只展示支持的 resolution，不支持的直接隐藏
  const resolutionOptionsFiltered = useMemo(() => {
    const byDuration = rules?.duration_resolution?.[duration];
    const byModel = internalModelId ? rules?.model_resolution?.[internalModelId] : undefined;
    return filterResolutionOptionsByRules(resolutionOptions, byDuration, byModel);
  }, [duration, resolutionOptions, rules, internalModelId]);

  // 当前选中的 resolution 不在支持列表中时，自动切到第一个支持的
  useEffect(() => {
    if (!showResolution || resolutionOptionsFiltered.length === 0) return;
    const supportedValues = resolutionOptionsFiltered.map((o) => o.value);
    if (resolution && !supportedValues.includes(resolution)) {
      dispatch(setDescParams({ resolution: resolutionOptionsFiltered[0].value }));
    }
  }, [showResolution, resolution, resolutionOptionsFiltered, dispatch]);

  // duration 变化时，联动修正 resolution：当前项可用就保留，否则切到第一个可用项
  const handleDurationChange = useCallback(
    (v: number) => {
      const updates: Partial<DescParamsData> = { duration: v };
      const allowed = rules?.duration_resolution?.[v] as string[];
      // 明确，这里一定是允许的，因为 duration 变化时，会触发 resolution 的自动修正
      // 不需要判断 const allowed = rules?.duration_resolution?.[v] as string[]; 数组
      // !allowed.includes(resolution)
      updates.resolution = allowed[0];
      dispatch(setDescParams(updates));
    },
    [dispatch, rules],
  );

  // resolution 变化时，联动修正 duration：当前项可用就保留，否则切到第一个可用项
  const handleResolutionChange = useCallback(
    (v: string) => {
      const updates: any = { resolution: v };
      const allowed = rules?.duration_resolution?.[v];
      if (allowed?.length && !allowed.includes(duration)) {
        updates.duration = allowed[0];
      }
      dispatch(setDescParams(updates));
    },
    [dispatch, duration, rules],
  );

  return (
    <>
      {/* Aspect Ratio */}
      <Select
        size="large"
        disabled={disabled}
        value={aspectRatio}
        onChange={(v) => dispatch(setDescParams({ aspectRatio: v }))}
        options={aspectRatioOptions}
        className="param-select w-[110px]"
        popupMatchSelectWidth={false}
        prefix={
          <IconDirection
            className={`w-[16px] h-[16px] text-kds-fg-subtle ${aspectRatio === "RATIO_16_9" ? "rotate-90" : ""}`}
          />
        }
        suffixIcon={<IconArrowDown className="w-[16px] h-[16px] text-kds-fg-subtle" />}
      />

      {/* Duration */}
      <Select
        size="large"
        disabled={disabled || disabledDuration}
        data-testid={TestId.VideoDurationSelect}
        value={duration}
        onChange={handleDurationChange}
        options={translatedDurationOptions}
        className="param-select w-[110px]"
        popupMatchSelectWidth={false}
        prefix={<IconTime className="w-[16px] h-[16px] text-kds-fg-subtle" />}
        suffixIcon={<IconArrowDown className="w-[16px] h-[16px] text-kds-fg-subtle" />}
      />

      {/* Quantity */}
      {/* {showQuantity && (
        <Select
          size="large"
          value={quantity}
          onChange={(v) => dispatch(setDescParams({ quantity: v }))}
          options={quantityOptions}
          className="param-select w-[110px]"
          popupMatchSelectWidth={false}
          suffixIcon={<IconArrowDown className="w-[16px] h-[16px] text-kds-fg-subtle" />}
        />
      )} */}

      {/* Resolution */}
      {showResolution && (
        <Select
          size="large"
          disabled={disabled}
          data-testid={TestId.VideoResolutionSelect}
          value={
            resolution && resolutionOptionsFiltered.some((o) => o.value === resolution)
              ? resolution
              : resolutionOptionsFiltered[0]?.value
          }
          onChange={handleResolutionChange}
          options={resolutionOptionsFiltered}
          className="param-select w-[110px]"
          popupMatchSelectWidth={false}
          suffixIcon={<IconArrowDown className="w-[16px] h-[16px] text-kds-fg-subtle" />}
        />
      )}

      {/* Model */}
      {showModel && <ModelSelect disabled={disabled} />}
    </>
  );
};
